import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
    Card,
    CardContent,
    CardHeader,
} from "../../components/ui/card";
import { Loader2, Check, AlertCircle, KeyRound, ShieldCheck } from "lucide-react";
import {
    InputOTP,
} from "../../components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { toast } from "../../hooks/use-toast";
import { authApi } from "../../api/auth.api";
import { translateBackendError } from "../../utils/errors";

export default function ResetPin() {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pin, setPin] = useState("");
    const [confirmPin, setConfirmPin] = useState("");
    const [isPinEntered, setIsPinEntered] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Basic validation of token on mount
    useEffect(() => {
        if (!token) {
            setError(t('auth:resetPin.invalidToken'));
        }
    }, [token, t]);

    // Handle PIN entry completion (first step)
    useEffect(() => {
        if (pin.length === 4 && !isPinEntered) {
            setIsPinEntered(true);
        }
    }, [pin, isPinEntered]);

    // Clear error when inputs change
    useEffect(() => {
        if (error && (pin || confirmPin)) {
            setError(null);
        }
    }, [pin, confirmPin, error]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            setError(t('auth:resetPin.tokenMissingMessage'));
            return;
        }

        if (pin.length !== 4) {
            setError(t('auth:signup.validation.pin4Digits'));
            return;
        }

        if (pin !== confirmPin) {
            setError(t('auth:signup.validation.pinMismatch'));
            setConfirmPin("");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await authApi.resetPin({
                token: token,
                newPin: pin,
            });

            setIsSuccess(true);
            toast({
                title: t('auth:resetPin.successTitle'),
                description: t('auth:resetPin.successMessage') + " " + t('auth:resetPin.redirecting'),
                type: "success",
            });

            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate("/login", {
                    state: { message: t('auth:resetPin.successTitle') }
                });
            }, 2000);
        } catch (err: any) {
            const rawMsg = err.response?.data?.message || err.message || t('auth:resetPin.error');
            setError(translateBackendError(rawMsg, i18n.language));
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setPin("");
        setConfirmPin("");
        setIsPinEntered(false);
    };

    // Render slot for OTP input
    const renderSlot = (slot: any, index: number, isActive: boolean) => (
        <div
            key={index}
            className={`w-12 h-14 border-2 rounded-lg flex items-center justify-center text-2xl font-bold transition-all ${slot.char
                ? "border-primary-600 bg-primary-50 text-primary-900"
                : isActive
                    ? "border-primary-500 bg-white ring-2 ring-primary-200"
                    : "border-gray-200 bg-white"
                }`}
        >
            {slot.char ? "•" : slot.hasFakeCaret && <div className="w-0.5 h-6 bg-primary-600 animate-pulse" />}
        </div>
    );

    // Initial Error state (mising token)
    if (!token && error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-100 to-gray-100 p-4">
                <Card className="w-full max-w-md shadow-2xl bg-white/90 backdrop-blur-md">
                    <CardHeader className="text-center pt-8">
                        <div className="bg-red-100 p-4 rounded-full w-fit mx-auto mb-4">
                            <AlertCircle className="h-10 w-10 text-red-600" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900">{t('auth:resetPin.invalidToken')}</h1>
                    </CardHeader>
                    <CardContent className="text-center pb-8">
                        <p className="text-gray-600 mb-6">{error}</p>
                        <Button onClick={() => navigate("/login")} className="w-full">
                            {t('auth:login.loginButton')}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Success state
    if (isSuccess) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-100 to-gray-100 p-4">
                <Card className="w-full max-w-md shadow-2xl bg-white/90 backdrop-blur-md">
                    <CardHeader className="text-center pt-8">
                        <div className="bg-green-100 p-4 rounded-full w-fit mx-auto mb-4">
                            <ShieldCheck className="h-10 w-10 text-green-600" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900">{t('auth:resetPin.successTitle')}</h1>
                    </CardHeader>
                    <CardContent className="text-center pb-8">
                        <p className="text-gray-600 mb-4">{t('auth:resetPin.successMessage')}</p>
                        <div className="flex items-center justify-center gap-2 text-green-600">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>{t('auth:resetPin.redirecting')}</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Main Reset PIN form
    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-100 to-gray-100 p-4">
            <Card className="w-full max-w-md shadow-2xl bg-white/90 backdrop-blur-md">
                <CardHeader className="flex flex-col items-center text-center space-y-4 pt-8">
                    <div className="bg-primary-100 p-3 rounded-full">
                        <KeyRound className="h-8 w-8 text-primary-600" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">{t('auth:resetPin.title')}</h1>
                        <p className="text-sm text-gray-500 mt-2">
                            {t('auth:resetPin.subtitle')}
                        </p>
                    </div>
                </CardHeader>

                <CardContent className="pt-4 pb-8">
                    {error && (
                        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                            <AlertCircle size={18} />
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* PIN Entry Container */}
                        <div className="grid grid-cols-1">
                            {/* First PIN Input */}
                            <div
                                className={`col-start-1 row-start-1 w-full transition-all duration-500 ease-in-out transform ${isPinEntered ? '-translate-x-full opacity-0 pointer-events-none' : 'translate-x-0 opacity-100'
                                    }`}
                            >
                                <div className="flex flex-col items-center space-y-6">
                                    <div className="space-y-2 text-center">
                                        <Label className="text-gray-700 font-semibold">{t('auth:resetPin.newPinLabel')}</Label>
                                        <InputOTP
                                            maxLength={4}
                                            value={pin}
                                            onChange={(value: string) => setPin(value)}
                                            containerClassName="flex justify-center"
                                            pattern={REGEXP_ONLY_DIGITS}
                                            render={({ slots }: { slots: any[] }) => (
                                                <div className="flex gap-3">
                                                    {slots.map((slot, i) => renderSlot(slot, i, slot.isActive))}
                                                </div>
                                            )}
                                            disabled={isLoading}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Confirm PIN Input */}
                            <div
                                className={`col-start-1 row-start-1 w-full transition-all duration-500 ease-in-out transform ${isPinEntered ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                                    }`}
                            >
                                <div className="flex flex-col items-center space-y-6">
                                    <div className="space-y-2 text-center">
                                        <Label className="text-gray-700 font-semibold">{t('auth:resetPin.confirmPinLabel')}</Label>
                                        <InputOTP
                                            maxLength={4}
                                            value={confirmPin}
                                            onChange={(value: string) => setConfirmPin(value)}
                                            containerClassName="flex justify-center"
                                            pattern={REGEXP_ONLY_DIGITS}
                                            render={({ slots }: { slots: any[] }) => (
                                                <div className="flex gap-3">
                                                    {slots.map((slot, i) => renderSlot(slot, i, slot.isActive))}
                                                </div>
                                            )}
                                            disabled={isLoading}
                                            autoFocus
                                        />
                                    </div>

                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={handleReset}
                                        disabled={isLoading}
                                        className="text-sm text-gray-500"
                                    >
                                        {t('auth:resetPin.backToFirstStep')}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="mt-8">
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={isLoading || pin.length !== 4 || confirmPin.length !== 4}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('auth:resetPin.resetting')}
                                    </>
                                ) : (
                                    <>
                                        <Check className="mr-2 h-4 w-4" />
                                        {t('auth:resetPin.resetButton')}
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>

                    <div className="mt-4">
                        <Button
                            variant="ghost"
                            className="w-full text-gray-500"
                            onClick={() => navigate("/login")}
                            disabled={isLoading}
                        >
                            {t('auth:login.loginButton')}
                        </Button>
                    </div>

                    {/* Security info */}
                    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                            {t('auth:resetPin.securityTip')}
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
